import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../css/productDetails.scss";
import Header from "./common/Header";
import Footer from "./common/Footer";
import { CartContext } from "./common/CartContext";
import CartDrawer from "./common/CartDrawer";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useParams } from "react-router-dom";
import { search_product_by_id } from "../apis/SearchProductById";
import { BASE_URL } from "../constans/ApiEndpointConstants";
import { sendMessage } from "../apis/SendMessageAPI";
import LocalStorageUtil from "../utils/localStorageUtil";
import { PixelWithEvent } from "./common/PixelWithEvent ";
import * as FacebookAPI from "../apis/FacebookAPI";
import Cookies from "js-cookie";
import { 
  isGbpLanguage, 
} from "../constans/CurrencyConstants";

const PdTrustCheckIcon = () => (
  <svg
    className="w-5 h-5 text-green-500"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const PdTrustLockIcon = () => (
  <svg
    className="pd-trust__lockSvg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill=" $shareColor"
      d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
    />
  </svg>
);

function PdShippingCutoff() {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [countryOpen, setCountryOpen] = useState(false);
  const countryRef = useRef(null);

  const cutoffHour = 14;
  const cutoffMinute = 0;

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!countryOpen) return undefined;
    const onDoc = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [countryOpen]);

  const { hoursLeft, minutesLeft } = useMemo(() => {
    const d = new Date(now);
    d.setHours(cutoffHour, cutoffMinute, 0, 0);
    if (now.getTime() >= d.getTime()) {
      d.setDate(d.getDate() + 1);
    }
    const diff = Math.max(0, d.getTime() - now.getTime());
    const hoursLeft = Math.floor(diff / 3600000);
    const minutesLeft = Math.floor((diff % 3600000) / 60000);
    return { hoursLeft, minutesLeft };
  }, [now, cutoffHour, cutoffMinute]);

  /** 固定显示 Next Day / Morgen，由 i18n 控制双语 */
  const deliveryRangeLabel = t("pd.cutoff.nextDay");

  const pad2 = (n) => String(n).padStart(2, "0");

  return (
    <div className="pd-cutoff">
      <p className="pd-cutoff__headline">
        {t("pd.cutoff.headline", { time: "14:00" })}
      </p>
      <p className="pd-cutoff__countdown" aria-live="polite">
        <span className="pd-cutoff__countMuted">{t("pd.cutoff.within")}</span>{" "}
        <strong className="pd-cutoff__countNum">{pad2(hoursLeft)}</strong>{" "}
        <span className="pd-cutoff__countMuted">{t("pd.cutoff.hours")}</span>{" "}
        <strong className="pd-cutoff__countNum">{pad2(minutesLeft)}</strong>{" "}
        <span className="pd-cutoff__countMuted">{t("pd.cutoff.minutes")}</span>
      </p>

      <div className="pd-cutoff__timeline" aria-hidden="true">
        <div className="pd-cutoff__track" />
        <div className="pd-cutoff__steps">
          <div className="pd-cutoff__step">
            <div className="pd-cutoff__iconBox pd-cutoff__iconBox--active">
              <svg
                className="pd-cutoff__checkSvg"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M9 16.17 4.83 12l-1.41 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                />
              </svg>
            </div>
            <div className="pd-cutoff__stepTitle">{t("pd.cutoff.ordered")}</div>
            <div className="pd-cutoff__stepSub">{t("pd.cutoff.today")}</div>
          </div>
          <div className="pd-cutoff__step">
            <div className="pd-cutoff__iconBox pd-cutoff__iconBox--pending">
              <svg
                className="pd-cutoff__lineSvg"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 8l8-4 8 4v8l-8 4-8-4V8z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 4v16M4 8l8 5 8-5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="pd-cutoff__stepTitle">{t("pd.cutoff.shipped")}</div>
            <div className="pd-cutoff__stepSub">{t("pd.cutoff.today")}</div>
          </div>
          <div className="pd-cutoff__step">
            <div className="pd-cutoff__iconBox pd-cutoff__iconBox--pending">
              <svg
                className="pd-cutoff__lineSvg"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 3L4 8v8l8 5 8-5V8l-8-5z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 23V12M4 8l8 5 8-5M12 12l8-5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="pd-cutoff__stepTitle">
              {t("pd.cutoff.delivered")}
            </div>
            <div className="pd-cutoff__stepSub pd-cutoff__stepSub--range">
              {deliveryRangeLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ProductDetails = () => {
  const { t, i18n } = useTranslation();
  const { name_id, id: legacyId } = useParams();
  // 解析路由参数：支持 /shopDetails/:name_id 和历史的 /shopDetails/:id
  // 最终取下划线分隔的最后一段作为产品 id，用于请求详情
  const parsedId = useMemo(() => {
    const raw = name_id || legacyId || "";
    if (!raw) return "";
    const parts = String(raw).split("_");
    return parts[parts.length - 1] || "";
  }, [name_id, legacyId]);
  // i18n 首帧可能尚未写入 language；与 init 默认 en 及 langSuffix 逻辑对齐，避免要刷新才变 £
  const resolvedI18nLang = useMemo(
    () => i18n.resolvedLanguage || i18n.language || "en",
    [i18n.resolvedLanguage, i18n.language]
  );
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [pagePinged, setPagePinged] = useState(false);
  const hasTriggeredViewContent = React.useRef(false);
  const [qty, setQty] = useState(1);
  const [showCart, setShowCart] = useState(false);
  const { addProduct, items = [] } = useContext(CartContext) || {};
  // 金额格式化：根据语言选择币种与本地化规则（全部使用 EUR）
  const formatEuro = React.useCallback(
    (n) => {
      const isUk = isGbpLanguage(resolvedI18nLang);
      const locale = isUk ? "en-GB" : "nl-NL";
      const currency = "EUR";
      const amount = n;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format(amount)
        .replace(/\u00A0/g, " ");
    },
    [resolvedI18nLang]
  );

  // 将相对路径补全为绝对 URL（拼接 BASE_URL），完整 URL 则原样返回
  const withBase = (src) => {
    if (!src) return null;
    if (/^https?:\/\//i.test(src) || src.startsWith("//")) return src;
    return `${BASE_URL}${src.startsWith("/") ? src : `/${src}`}`;
  };
  // 根据当前语言选择字段后缀：nl 使用 _nl，其它使用 _uk
  const langSuffix = useMemo(
    () => (resolvedI18nLang.toLowerCase() === "nl" ? "nl" : "uk"),
    [resolvedI18nLang]
  );
  // 多语言字段读取工具：优先 base_{lang}，其次 base/base_en/base_uk
  const byLang = React.useCallback(
    (obj, base) =>
      obj?.[`${base}_${langSuffix}`] ??
      obj?.[base] ??
      obj?.[`${base}_en`] ??
      obj?.[`${base}_uk`] ??
      null,
    [langSuffix]
  );
  const norm = (s) => (s == null ? "" : String(s).trim());

  // SKU 列表（兼容 skulist/skuList）
  const skus = useMemo(
    () =>
      Array.isArray(product?.skulist)
        ? product.skulist
        : Array.isArray(product?.skuList)
        ? product.skuList
        : [],
    [product]
  );
  // 收集所有 SKU 价格（多语言字段优先），用于计算价格区间
  const skuPrices = useMemo(
    () =>
      skus
        .map((s) => Number(s?.[`${"price"}_${langSuffix}`] ?? s?.price))
        .filter((n) => !isNaN(n) && n > 0),
    [skus, langSuffix]
  );
  // 计算最小价/最大价与区间展示（未选定具体 SKU 时使用）
  const minPrice = useMemo(() => {
    if (skuPrices.length > 0) return Math.min(...skuPrices);
    const base =
      byLang(product, "price") ??
      product?.sale_price ??
      product?.priceNew ??
      product?.price_new ??
      product?.price_min ??
      product?.min_price ??
      null;
    const n = Number(base);
    return !isNaN(n) && n > 0 ? n : 0;
  }, [skuPrices, product, byLang]);

  //计算规格最大金额与最小金额
  const maxPrice = useMemo(() => {
    if (skuPrices.length > 0) return Math.max(...skuPrices);
    return minPrice;
  }, [skuPrices, minPrice]);
  const priceDisplay = useMemo(() => {
    if (minPrice && maxPrice && minPrice !== maxPrice) {
      return `${formatEuro(minPrice)} - ${formatEuro(maxPrice)}`;
    }
    return minPrice ? formatEuro(minPrice) : "-";
  }, [minPrice, maxPrice, formatEuro]);
  const price = minPrice;
  // 展示名称：优先 name_{lang}，否则 title
  const displayName = useMemo(
    () => byLang(product, "name") ?? product?.title ?? "-",
    [product, byLang]
  );

  // 分类名称：优先 categoryname_{lang}
  const categoryNameDisplay = useMemo(
    () => byLang(product, "categoryname") || "",
    [product, byLang]
  );

  // Pixel 配置（从 search_product_by_id 接口的 data.pixel / data.token 获取）
  const pixel = product?.pixel ?? null;
  const fbqToken = product?.fbqtoken ?? null;

  // 生成/读取 event_id（按事件类型缓存 1 天）
  const getEventId = (eventType) => {
    const cookieName = `fb_event_id_${eventType}`;
    let stored = Cookies.get(cookieName);
    if (stored) return stored;
    const id = Math.round(Math.random() * 1e10).toString();
    Cookies.set(cookieName, id, { expires: 1 });
    return id;
  };

  // 进入 ProductDetails 页面上报一次
  useEffect(() => {
    if (pagePinged) return;
    const gen = () => {
      const ch =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let r = "";
      for (let i = 0; i < 6; i++)
        r += ch[Math.floor(Math.random() * ch.length)];
      return r;
    };
    let userid = localStorage.getItem("userid");
    if (!userid) {
      userid = gen();
      localStorage.setItem("userid", userid);
    }
    const ad = LocalStorageUtil.getAd?.() ?? "PurityBase";
    const productid = parsedId || product?.id || product?._id || "";
    sendMessage(
      `${BASE_URL} [Product Details Page]:ad=${ad}:userid=${userid}:id=${productid}`
    );
    setPagePinged(true);
  }, [pagePinged, parsedId, product]);

  // 将所有 SKU 的属性按“属性名”分组，得到每组的去重可选值列表
  const attrGroups = useMemo(() => {
    const map = new Map();
    skus.forEach((sku) => {
      const attrs = Array.isArray(sku?.attributeslist)
        ? sku.attributeslist
        : Array.isArray(sku?.attributesList)
        ? sku.attributesList
        : [];
      attrs.forEach((a) => {
        const n = norm(
          a?.[`attr_name_${langSuffix}`] ?? a?.attr_name ?? a?.attrName
        );
        const v = norm(
          a?.[`attr_value_${langSuffix}`] ?? a?.attr_value ?? a?.attrValue
        );
        if (!n || !v) return;
        if (!map.has(n)) map.set(n, new Set());
        map.get(n).add(v);
      });
    });
    return Array.from(map.entries()).map(([name, set]) => ({
      name,
      values: Array.from(set),
    }));
  }, [skus, langSuffix]);
  const [selectedAttrs, setSelectedAttrs] = useState({});
  // 记住当前选中的 SKU，切换语言时属性名会随 nl/uk 后缀变化，需按同一 SKU 重建 selectedAttrs
  const selectedSkuIdRef = React.useRef(null);
  useEffect(() => {
    selectedSkuIdRef.current = null;
  }, [parsedId]);
  // 根据当前 selectedAttrs 在所有 SKU 中查找完全匹配的 SKU
  const selectedSku = useMemo(() => {
    if (!skus.length || !Object.keys(selectedAttrs).length) return null;
    return (
      skus.find((sku) => {
        const attrs = Array.isArray(sku?.attributeslist)
          ? sku.attributeslist
          : Array.isArray(sku?.attributesList)
          ? sku.attributesList
          : [];
        const pairs = {};
        attrs.forEach((a) => {
          const n = norm(
            a?.[`attr_name_${langSuffix}`] ?? a?.attr_name ?? a?.attrName
          );
          const v = norm(
            a?.[`attr_value_${langSuffix}`] ?? a?.attr_value ?? a?.attrValue
          );
          if (n && v) pairs[n] = v;
        });
        return Object.entries(selectedAttrs).every(
          ([k, v]) => pairs[norm(k)] === norm(v)
        );
      }) || null
    );
  }, [skus, selectedAttrs, langSuffix]);

  useEffect(() => {
    if (!selectedSku) return;
    const id = selectedSku.sku_id ?? selectedSku.id ?? selectedSku.skuId;
    if (id != null && String(id).trim() !== "") selectedSkuIdRef.current = id;
  }, [selectedSku]);

  // 在当前其它维度选择不变的前提下，某规格值是否与「有货 SKU」存在组合（stock === 0 视为无货不可选）
  const isAttrValueAvailable = React.useCallback(
    (attrName, candidateValue) => {
      const dim = norm(attrName);
      const val = norm(candidateValue);
      return skus.some((sku) => {
        if (Number(sku?.stock) === 0) return false;
        const attrs = Array.isArray(sku?.attributeslist)
          ? sku.attributeslist
          : Array.isArray(sku?.attributesList)
          ? sku.attributesList
          : [];
        const pairs = {};
        attrs.forEach((a) => {
          const n = norm(
            a?.[`attr_name_${langSuffix}`] ?? a?.attr_name ?? a?.attrName
          );
          const v = norm(
            a?.[`attr_value_${langSuffix}`] ?? a?.attr_value ?? a?.attrValue
          );
          if (n && v) pairs[n] = v;
        });
        if (pairs[dim] !== val) return false;
        return Object.entries(selectedAttrs).every(([k, sel]) => {
          if (norm(k) === dim) return true;
          return pairs[norm(k)] === norm(sel);
        });
      });
    },
    [skus, selectedAttrs, langSuffix]
  );

  // 语言或 SKU 列表变化时同步规格状态（layout 阶段执行，避免与下方 merge 等 effect 抢状态）
  useLayoutEffect(() => {
    if (!skus.length) {
      setSelectedAttrs({});
      return;
    }
    const id = selectedSkuIdRef.current;
    let target = skus[0];
    if (id != null && String(id).trim() !== "") {
      const sid = String(id);
      const found = skus.find(
        (s) => String(s.sku_id ?? s.id ?? s.skuId ?? "") === sid
      );
      if (found) target = found;
    }
    const attrs = Array.isArray(target?.attributeslist)
      ? target.attributeslist
      : Array.isArray(target?.attributesList)
      ? target.attributesList
      : [];
    const init = {};
    attrs.forEach((a) => {
      const n = norm(
        a?.[`attr_name_${langSuffix}`] ?? a?.attr_name ?? a?.attrName
      );
      const v = norm(
        a?.[`attr_value_${langSuffix}`] ?? a?.attr_value ?? a?.attrValue
      );
      if (n && v) init[n] = v;
    });
    setSelectedAttrs(init);
  }, [langSuffix, skus]);
  // 已选 SKU 的实价展示（若未选 SKU 则回退到区间价）
  const priceDisplayFinal = useMemo(() => {
    if (selectedSku) {
      const n = Number(
        selectedSku?.[`${"price"}_${langSuffix}`] ?? selectedSku?.price
      );
      return !isNaN(n) && n > 0 ? formatEuro(n) : "-";
    }
    // 区间价已经在 minPrice / maxPrice 处打了 1 折，直接使用 priceDisplay
    return priceDisplay;
  }, [selectedSku, priceDisplay, langSuffix, formatEuro]);
  
  // 已选 SKU 的数值价格，用于加入购物车等运算
  const priceFinal = useMemo(() => {
    if (selectedSku) {
      const n = Number(
        selectedSku?.[`${"price"}_${langSuffix}`] ?? selectedSku?.price
      );
      return !isNaN(n) && n > 0 ? n : price;
    }
    return price;
  }, [selectedSku, langSuffix, price]);

  const handleAddToCart = () => {
    const gen = () => {
      const ch =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let r = "";
      for (let i = 0; i < 6; i++)
        r += ch[Math.floor(Math.random() * ch.length)];
      return r;
    };
    let userid = localStorage.getItem("userid");
    if (!userid) {
      userid = gen();
      localStorage.setItem("userid", userid);
    }
    const ad = LocalStorageUtil.getAd?.() ?? "PurityBase";
    const productid = parsedId || product?.id || product?._id || "";
    const skuid = (selectedSku && (selectedSku.sku_id || selectedSku.id)) || "";
    const unitPrice = priceFinal;
    // id 使用商品 ID（与详情页/接口一致）；多规格时用 lineKey 唯一标识一行
    const productId = parsedId || (product?.id ?? product?._id ?? "");
    const lineKey = skuid
      ? `${productId}_${skuid}`
      : String(productId || Date.now());
    const item = {
      id: productId || Date.now(),
      skuId: skuid || undefined,
      lineKey,
      title: displayName,
      thumb: images[0],
      options: Object.entries(selectedAttrs).map(([k, v]) => `${k}: ${v}`),
      price: unitPrice,
      qty,
    };
    if (typeof addProduct === "function") addProduct(item);
    sendMessage(
      `${BASE_URL} [Add To Cart]:ad=${ad}:userid=${userid}:productid=${productid}:skuid=${skuid}:qty=${qty}:price=${unitPrice}`
    );
    const event_id = getEventId("AddToCart");
    const currency = isGbpLanguage(resolvedI18nLang) ? "GBP" : "EUR";
    const contentId = skuid || productid || "product";
    const eventData = {
      content_ids: [String(contentId)],
      content_name: displayName || "Product",
      content_category: "product",
      value: Number(unitPrice) * Number(qty || 1),
      currency,
      num_items: Number(qty || 1),
      contents: [
        {
          id: String(contentId),
          title: displayName || "Product",
          quantity: Number(qty || 1),
        },
      ],
    };
    PixelWithEvent("AddToCart", event_id);
    if (pixel && fbqToken) {
      FacebookAPI.Facebook(fbqToken, pixel, event_id, "AddToCart", eventData);
    }
    setShowCart(true);
  };

  // 选择 SKU/规格时上报 CustomizeProduct
  const lastCustomizeKeyRef = React.useRef("");
  useEffect(() => {
    if (!product) return;
    // 构造一个 key 判断是否真的发生了有效变更
    const skuid = (selectedSku && (selectedSku.sku_id || selectedSku.id)) || "";
    const optsKey = Object.entries(selectedAttrs)
      .map(([k, v]) => `${k}=${v}`)
      .sort()
      .join("&");
    const currentKey = `${parsedId || product?.id || ""}|${skuid}|${optsKey}`;
    if (!skuid && !optsKey) return;
    if (currentKey === lastCustomizeKeyRef.current) return;
    lastCustomizeKeyRef.current = currentKey;

    const event_id = getEventId("CustomizeProduct");
    const currency = isGbpLanguage(resolvedI18nLang) ? "GBP" : "EUR";
    const contentId = skuid || parsedId || product?.id || "product";
    const eventData = {
      content_ids: [String(contentId)],
      content_name: displayName || "Product",
      content_category: "product",
      value: Number(priceFinal) || Number(minPrice) || 0,
      currency,
      num_items: 1,
      contents: [
        {
          id: String(contentId),
          title: displayName || "Product",
          quantity: 1,
        },
      ],
    };
    PixelWithEvent("CustomizeProduct", event_id);
    if (pixel && fbqToken) {
      FacebookAPI.Facebook(
        fbqToken,
        pixel,
        event_id,
        "CustomizeProduct",
        eventData
      );
    }
  }, [
    selectedSku,
    selectedAttrs,
    product,
    displayName,
    priceFinal,
    minPrice,
    resolvedI18nLang,
    parsedId,
    pixel,
    fbqToken,
  ]);

  // ViewContent 上报：停留 8s 或滚动超过 50px 触发一次
  useEffect(() => {
    const trigger = () => {
      if (hasTriggeredViewContent.current) return;
      hasTriggeredViewContent.current = true;
      const event_id = getEventId("ViewContent");
      const currency = isGbpLanguage(resolvedI18nLang) ? "GBP" : "EUR";
      const skuid =
        (selectedSku && (selectedSku.sku_id || selectedSku.id)) || "";
      const contentId = skuid || parsedId || product?.id || "product";
      const eventData = {
        content_ids: [String(contentId)],
        content_name: displayName || "Product",
        content_category: "product",
        value: Number(priceFinal) || Number(minPrice) || 0,
        currency,
        num_items: 1,
        contents: [
          {
            id: String(contentId),
            title: displayName || "Product",
            quantity: 1,
          },
        ],
      };
      PixelWithEvent("ViewContent", event_id);
      if (pixel && fbqToken) {
        FacebookAPI.Facebook(
          fbqToken,
          pixel,
          event_id,
          "ViewContent",
          eventData
        );
      }
    };
    const timer = setTimeout(trigger, 8000);
    const handleScroll = () => {
      if (window.scrollY > 50) trigger();
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    pixel,
    fbqToken,
    parsedId,
    product,
    selectedSku,
    displayName,
    priceFinal,
    minPrice,
    resolvedI18nLang,
  ]);
  // 拉取商品详情（按解析出的 parsedId）
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!parsedId) return;
      setLoading(true);
      try {
        const res = await search_product_by_id(parsedId);
        const data = res?.data?.data || res?.data?.product || res?.data || null;
        if (!alive) return;
        setProduct(data);
        const productlinkArr = Array.isArray(data?.productlink)
          ? data.productlink
              .map((l) =>
                typeof l === "string"
                  ? l
                  : l?.url || l?.path || l?.fileurl || l?.link || null
              )
              .filter(Boolean)
          : [];
        const imgs = []
          .concat(
            productlinkArr,
            data?.images && Array.isArray(data.images) ? data.images : [],
            data?.image ? [data.image] : [],
            data?.cover ? [data.cover] : [],
            data?.thumbnail ? [data.thumbnail] : [],
            data?.thumb ? [data.thumb] : []
          )
          .filter(Boolean)
          .map(withBase);
        setImages(imgs.length > 0 ? imgs : ["/main/main1.avif"]);
        setActiveImg(0);
      } catch (e) {
        if (!alive) return;
        setProduct(null);
        setImages(["/main/main1.avif"]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [parsedId]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 详情页路由变化时滚动到顶部
  useEffect(() => {
    toTop();
  }, [parsedId]);

  return (
    <div>
      <Header />
      {loading ? (
        <main className="pd-page">
          <div className="pd__container">
            <div className="pd-loading" role="status" aria-live="polite">
              <span className="spinner" aria-hidden="true"></span>
              <span className="sr-only">
                {t("common.loading") || "Loading..."}
              </span>
            </div>
          </div>
        </main>
      ) : (
        <main className="pd-page">
          <div className="pd__container">
            <div className="pd__layout">
              <section className="pd-gallery" aria-label="Product gallery">
                <nav className="pd__crumbs" aria-label="breadcrumbs">
                  <span className="yellow_color">{t("pd.crumbs.home")}</span>{" "}
                  <span>/</span>{" "}
                   <span className="yellow_color">{t("pd.crumbs.products")}</span>{" "}
                  {categoryNameDisplay ? (
                    <>
                      <span>/</span>{" "}
                      <span className="yellow_color">{categoryNameDisplay}</span>
                    </>
                  ) : null}
                </nav>
                <div className="pd-gallery__view">
                  <img src={images[activeImg]} alt="product" />
                </div>
                <div className="pd-gallery__thumbs">
                  {images.map((src, i) => (
                    <button
                      key={`thumb-${i}`}
                      className={`pd-thumb ${activeImg === i ? "active" : ""}`}
                      onClick={() => setActiveImg(i)}
                    >
                      <img src={src} alt={`thumb-${i}`} />
                    </button>
                  ))}
                </div>
              </section>

              <section className="pd-info" aria-label="Product options">
                <h1 className="pd__title">{displayName}</h1>
                {product?.status === 1 && (
                  <div className="pd-outOfStockAlert">
                    <span className="pd-outOfStockAlert__icon">✗</span>
                    {t("main.buttons.outOfStock", "Out of Stock")}
                  </div>
                )}
                <div className="pd__price-spot">{priceDisplay}</div>

                {attrGroups.map((g) => (
                  <div className="pd-opt" key={`attr-${g.name}`}>
                    <div className="pd-opt__label">
                      {g.name}
                      {selectedAttrs[g.name] ? (
                        <>
                          {" : "}
                          <span className="pd-opt__labelValue">
                            {selectedAttrs[g.name]}
                          </span>
                        </>
                      ) : null}
                    </div>
                    <div className="pd-opt__row">
                      {g.values.map((v) => {
                        const nv = norm(v);
                        const isCurrent = norm(selectedAttrs[g.name]) === nv;
                        const available = isAttrValueAvailable(g.name, v);
                        const disabled = !available && !isCurrent;
                        return (
                          <button
                            key={`attr-${g.name}-${v}`}
                            type="button"
                            className={`pd-btn ${isCurrent ? "active" : ""}`}
                            disabled={disabled}
                            onClick={() =>
                              setSelectedAttrs((prev) => ({
                                ...prev,
                                [g.name]: nv,
                              }))
                            }
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="pd-purchase">
                  <div className="pd-purchase__hline" aria-hidden="true" />
                  <div className="pd-purchase__qtyRow">
                    <span className="pd-purchase__fieldLabel">
                      {t("pd.purchase.quantity")}
                    </span>
                    <div
                      className="pd-qtyStepper"
                      role="group"
                      aria-label={t("pd.purchase.quantity")}
                    >
                      <button
                        type="button"
                        className="pd-qtyStepper__btn"
                        onClick={() =>
                          setQty((q) => Math.max(1, Number(q) - 1))
                        }
                        aria-label={t("pd.purchase.decreaseQty")}
                      >
                        −
                      </button>
                      <span className="pd-qtyStepper__value" aria-live="polite">
                        {qty}
                      </span>
                      <button
                        type="button"
                        className="pd-qtyStepper__btn"
                        onClick={() =>
                          setQty((q) => Math.max(1, Number(q) + 1))
                        }
                        aria-label={t("pd.purchase.increaseQty")}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="pd-purchase__hline" aria-hidden="true" />
                  <div className="pd-purchase__priceRow">
                    <span className="pd-purchase__fieldLabel">
                      {t("pd.purchase.price")}
                    </span>
                    <span className="pd-purchase__priceValue">
                      {priceDisplayFinal}
                    </span>
                  </div>
                  <div className="pd-purchase__hline" aria-hidden="true" />
                  <div className="pd-purchase__stock">
                    {product?.status === 1 ? "" : t("pd.purchase.inStock")}
                  </div>
                  <div className="pd-purchase__row">
                    <button 
                      className={`pd-add ${product?.status === 1 ? "disabled" : ""}`} 
                      onClick={product?.status === 1 ? undefined : handleAddToCart}
                      disabled={product?.status === 1}
                    >
                      {product?.status === 1 ? t("main.buttons.outOfStock", "Out of Stock") : t("pd.purchase.addToCart")}
                    </button>
                  </div>
                  <div className="pd-trust">
                    <ul className="pd-trust__benefits">
                      <li className="pd-trust__benefit">
                        <span className="pd-trust__check" aria-hidden="true">
                          <PdTrustCheckIcon />
                        </span>
                        <span className="pd-trust__benefitText">
                          {t("pd.shipping.b1")}
                        </span>
                      </li>
                      <li className="pd-trust__benefit">
                        <span className="pd-trust__check" aria-hidden="true">
                          <PdTrustCheckIcon />
                        </span>
                        <span className="pd-trust__benefitText">
                          {t("pd.shipping.b2")}
                        </span>
                      </li>
                      <li className="pd-trust__benefit">
                        <span className="pd-trust__check" aria-hidden="true">
                          <PdTrustCheckIcon />
                        </span>
                        <span className="pd-trust__benefitText">
                          {t("pd.shipping.b3")}
                        </span>
                      </li>
                    </ul>
                    <div className="pd-trust__secure">
                      <div className="pd-trust__secureHead">
                        <PdTrustLockIcon />
                        <span className="pd-trust__secureTitle">
                          {t("pd.shipping.securePayments")}
                        </span>
                      </div>
                      <div
                        className="pd-trust__payRow"
                        role="group"
                        aria-label={t("pd.shipping.securePayments")}
                      >
                        <div
                          className="pd-trust__payBadge"
                          role="img"
                          aria-label="Visa"
                        >
                          <svg
                            viewBox="0 0 64 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="64"
                              height="40"
                              rx="4"
                              fill="#1A1F71"
                            />
                            <text x="32" y="26" textAnchor="middle" fill="#ffffff"
                              fontSize="13"
                              fontWeight="700"
                              fontFamily="Arial, Helvetica, sans-serif"
                            >
                              VISA
                            </text>
                          </svg>
                        </div>
                        <div
                          className="pd-trust__payBadge"
                          role="img"
                          aria-label="Mastercard"
                        >
                          <svg
                            viewBox="0 0 64 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="64"
                              height="40"
                              rx="4"
                              fill="#163788"
                            />
                            <circle cx="27" cy="20" r="11" fill="#EB001B" />
                            <circle
                              cx="37"
                              cy="20"
                              r="11"
                              fill="#F79E1B"
                              opacity="0.95"
                            />
                          </svg>
                        </div>
                        <div
                          className="pd-trust__payBadge"
                          role="img"
                          aria-label="iDEAL"
                        >
                          <svg
                            viewBox="0 0 64 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="64"
                              height="40"
                              rx="4"
                              fill="#1a1a1a"
                            />
                            <rect
                              x="0"
                              y="28"
                              width="64"
                              height="12"
                              fill="#CC0066"
                            />
                            <text x="32" y="20" textAnchor="middle" fill="#ffffff"
                              fontSize="10"
                              fontWeight="700"
                              fontFamily="Arial, Helvetica, sans-serif"
                            >
                              iDEAL
                            </text>
                          </svg>
                        </div>
                        <div
                          className="pd-trust__payBadge"
                          role="img"
                          aria-label="PayPal"
                        >
                          <svg
                            viewBox="0 0 64 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="64"
                              height="40"
                              rx="4"
                              fill="#003087"
                            />
                            <text
                              x="32"
                              y="25"
                              textAnchor="middle"
                              fill="#ffffff"
                              fontSize="9"
                              fontWeight="700"
                              fontFamily="Arial, Helvetica, sans-serif"
                            >
                              PayPal
                            </text>
                          </svg>
                        </div>
                        <div
                          className="pd-trust__payBadge"
                          role="img"
                          aria-label="Klarna"
                        >
                          <svg
                            viewBox="0 0 64 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="64"
                              height="40"
                              rx="4"
                              fill="#FFB3C7"
                            />
                            <text
                              x="32"
                              y="25"
                              textAnchor="middle"
                              fill="#0a0a0a"
                              fontSize="8"
                              fontWeight="700"
                              fontFamily="Arial, Helvetica, sans-serif"
                            >
                              Klarna.
                            </text>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <PdShippingCutoff />
                </div>
              </section>
            </div>

            {/* productDetails */}
            <section className="pd-details">
              <Tabs product={product} />
            </section>

            {/* relatedProducts */}
            {/* relatedProducts 保持静态示例，不改动路由结构以避免干扰当前实现 */}

            <CartDrawer
              open={showCart}
              onClose={() => setShowCart(false)}
              items={items}
              image={"/main/main1.avif"}
              title={"Winkelwagen"}
              options={[]}
              qty={0}
              price={0}
              subtotal={0}
            />
          </div>
        </main>
      )}
      <Footer />
    </div>
  );
};

const Tabs = ({ product }) => {
  const [tab, setTab] = useState("desc");
  const { t, i18n } = useTranslation();
  const resolvedI18nLang = i18n.resolvedLanguage || i18n.language || "en";
  const langSuffix = resolvedI18nLang.toLowerCase() === "nl" ? "nl" : "uk";
  const byLang = (obj, base) =>
    obj?.[`${base}_${langSuffix}`] ??
    obj?.[base] ??
    obj?.[`${base}_en`] ??
    obj?.[`${base}_uk`] ??
    "";
  const rawDesc = byLang(product || {}, "description") || "";
  const decodedDesc = useMemo(() => {
    if (typeof window === "undefined") return rawDesc;
    const el = document.createElement("textarea");
    el.innerHTML = rawDesc;
    return el.value;
  }, [rawDesc]);
  const descHtml = useMemo(() => {
    // Remove legacy <font> wrappers while preserving text
    const noFont = decodedDesc
      .replace(/<\/?font[^>]*>/gi, "")
      .replace(/\sstyle="[^"]*"/gi, "")
      .replace(/\sdir="[^"]*"/gi, "");
    return DOMPurify.sanitize(noFont, {
      ALLOWED_TAGS: [
        "h1",
        "h2",
        "h3",
        "h4",
        "p",
        "ul",
        "li",
        "strong",
        "b",
        "em",
        "br",
        "span",
        "a",
        "table",
        "caption",
        "colgroup",
        "col",
        "thead",
        "tbody",
        "tfoot",
        "tr",
        "th",
        "td",
      ],
      ALLOWED_ATTR: [
        "colspan",
        "rowspan",
        "scope",
        "href",
        "target",
        "rel",
        "title",
      ],
    });
  }, [decodedDesc]);
  const isUk = isGbpLanguage(i18n.language);
  const SHIP_EUR = 4.95;
  const formatShip = () => `€${SHIP_EUR.toFixed(2).replace(".", ",")}`;
  const formatThreshold = () => `€250`;
  const nextDayText = t("pd.cutoff.nextDay");
  const shipLines = [
    isUk
      ? t("pd.shipTab.ukRegion", { nextDay: nextDayText, price: formatShip() })
      : t("pd.shipTab.nlBeRegion", { price: formatShip() }),
    t("pd.shipTab.restEurope", { price: formatShip() }),
    t("pd.shipTab.originNote"),
    isUk
      ? t("pd.shipTab.freeShippingUk", { threshold: formatThreshold() })
      : t("pd.shipTab.freeShippingEu"),
  ];
  const useLines = t("pd.useTab.list", { returnObjects: true }) || [];
  const ChevronIcon = () => (
    <svg
      className="pd-acc__chevSvg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className="pd-acc">
      <div className={`pd-acc__item${tab === "desc" ? " is-open" : ""}`}>
        <button
          type="button"
          id="pd-acc-trigger-desc"
          className="pd-acc__head"
          aria-expanded={tab === "desc"}
          aria-controls="pd-acc-panel-desc"
          onClick={() => setTab((v) => (v === "desc" ? "" : "desc"))}
        >
          <span className="pd-acc__title">{t("pd.tabs.desc")}</span>
          <span className="pd-acc__chev">
            <ChevronIcon />
          </span>
        </button>
        <div
          id="pd-acc-panel-desc"
          role="region"
          aria-labelledby="pd-acc-trigger-desc"
          className={`pd-acc__body${tab === "desc" ? " is-open" : ""}`}
          hidden={tab !== "desc"}
        >
          <div className="pd-acc__inner">
            <div
              className="pd-desc"
              dangerouslySetInnerHTML={{ __html: descHtml }}
            />
          </div>
        </div>
      </div>

      <div className={`pd-acc__item${tab === "ship" ? " is-open" : ""}`}>
        <button
          type="button"
          id="pd-acc-trigger-ship"
          className="pd-acc__head"
          aria-expanded={tab === "ship"}
          aria-controls="pd-acc-panel-ship"
          onClick={() => setTab((v) => (v === "ship" ? "" : "ship"))}
        >
          <span className="pd-acc__title">{t("pd.tabs.ship")}</span>
          <span className="pd-acc__chev">
            <ChevronIcon />
          </span>
        </button>
        <div
          id="pd-acc-panel-ship"
          role="region"
          aria-labelledby="pd-acc-trigger-ship"
          className={`pd-acc__body${tab === "ship" ? " is-open" : ""}`}
          hidden={tab !== "ship"}
        >
          <div className="pd-acc__inner">
            <ul className="pd-acc__list">
              {shipLines.map((li, i) => (
                <li key={`sl-${i}`} dangerouslySetInnerHTML={{ __html: li }} />
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className={`pd-acc__item${tab === "lab" ? " is-open" : ""}`}>
        <button
          type="button"
          id="pd-acc-trigger-lab"
          className="pd-acc__head"
          aria-expanded={tab === "lab"}
          aria-controls="pd-acc-panel-lab"
          onClick={() => setTab((v) => (v === "lab" ? "" : "lab"))}
        >
          <span className="pd-acc__title">{t("pd.tabs.lab")}</span>
          <span className="pd-acc__chev">
            <ChevronIcon />
          </span>
        </button>
        <div
          id="pd-acc-panel-lab"
          role="region"
          aria-labelledby="pd-acc-trigger-lab"
          className={`pd-acc__body${tab === "lab" ? " is-open" : ""}`}
          hidden={tab !== "lab"}
        >
          <div className="pd-acc__inner">
            <p>{t("pd.labTab.p1")}</p>
            <p>
              <a className="pd-link" href="/lab">
                {t("pd.labTab.link")}
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className={`pd-acc__item${tab === "use" ? " is-open" : ""}`}>
        <button
          type="button"
          id="pd-acc-trigger-use"
          className="pd-acc__head"
          aria-expanded={tab === "use"}
          aria-controls="pd-acc-panel-use"
          onClick={() => setTab((v) => (v === "use" ? "" : "use"))}
        >
          <span className="pd-acc__title">{t("pd.tabs.use")}</span>
          <span className="pd-acc__chev">
            <ChevronIcon />
          </span>
        </button>
        <div
          id="pd-acc-panel-use"
          role="region"
          aria-labelledby="pd-acc-trigger-use"
          className={`pd-acc__body${tab === "use" ? " is-open" : ""}`}
          hidden={tab !== "use"}
        >
          <div className="pd-acc__inner">
            <ul className="pd-acc__list">
              {useLines.map((li, i) => (
                <li key={`ul-${i}`} dangerouslySetInnerHTML={{ __html: li }} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
